import { dialogService } from '@/services/dialog.service';

document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
window.addEventListener('resize', () => {
  document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
});
// TODO: troppi import statici -> convertire ad import dinamici dove possibile

/**
 * BARE IMPORTS
 */
import '@/assets/tailwind.css';
import '@/assets/skeleton-loader.css';
import '@/i18n/yup';
import 'utils/yup-custom-methods';

/**
 * PLUGINS
 * @see { loadPlugins }
 */
import { envPlugin } from '@/plugins/env';
import { ctxPlugin } from '@/plugins/context';
import { piniaPlugin } from '@/plugins/pinia';
import routerPlugin from '@/router';
import { vuetify } from '@/plugins/vuetify';
import PortalVue from 'portal-vue';
import { createGmapVuePlugin } from '@gmap-vue/v3';

/**
 * DIRECTIVES
 * @see { loadDirectives }
 */
import { intersect } from '@/directives/intersect';
import { clickOutside } from '@/directives/click-outside';
import { ripple } from '@/directives/ripple';

/**
 * SHARED COMPONENTS
 * @see { loadSharedComponents }
 */
import SharedAvatar from '@/components/_shared/SharedAvatar.vue';
import SharedAlert from '@/components/_shared/SharedAlert.vue';
import SharedBack from '@/components/_shared/SharedBack.vue';
import SharedBtn from '@/components/_shared/SharedBtn.vue';
import SharedComboBox from '@/components/_shared/SharedComboBox.vue';
import SharedLabeledText from '@/components/_shared/SharedLabeledText.vue';
import SharedLoading from '@/components/_shared/SharedLoading.vue';
import SharedLazyIterator from '@/components/_shared/SharedLazyIterator.vue';
import SharedModal from '@/components/_shared/SharedModal.vue';
import SharedNotFound from '@/components/_shared/SharedNotFound.vue';
import SharedOverlay from '@/components/_shared/SharedOverlay.vue';
import SharedProgressCircular from '@/components/_shared/SharedProgressCircular.vue';
import SharedRefresh from '@/components/_shared/SharedRefresh.vue';
import SharedScrollList from '@/components/_shared/SharedScrollList.vue';
import SharedTeleport from '@/components/_shared/SharedTeleport.vue';
import SharedTextInput from '@/components/_shared/SharedTextInput.vue';
import SharedTooltip from '@/components/_shared/SharedTooltip.vue';
import SharedTextTruncated from '@/components/_shared/SharedTextTruncated.vue';
import Dialog from '@/components/_shared/Dialog.vue';

/**
 * SERVICES
 */
import { errorService } from '@/services/error.service';

/**
 * LIB
 */
import { pipe } from '@/lib/functions';

/**
 * LAYOUTS
 * @see { loadLayouts }
 */
import PrivateLayout from '@/components/layouts/LayoutPrivate.vue';
import PublicLayout from '@/components/layouts/LayoutPublic.vue';
import ErrorLayout from '@/components/layouts/LayoutError.vue';

/**
 * SENTRY
 * */
import { init, captureException } from '@sentry/vue';
import { SentryConfig } from '../../../config/sentry';

/**
 * APP
 * @see { mountApp }
 */
import App from './App.vue';

import { createApp } from 'vue';
import { versionListener } from '@/services/versionListener';
import { scrollbarActiveClass } from '@/directives/scrollbarActiveClass';
import { onScrollEnd } from '@/directives/on-scroll-end';

const app = createApp(App);

init({
  app,
  ...SentryConfig.setStrategy(env['VITE_ENV'] as 'development' | 'staging' | 'production').get(),
});

function config() {
  app.config.errorHandler = (err) => {
    errorService.notifyError(err);
    captureException(err);
  };
}

function loadSharedComponents() {
  app.component('SharedAlert', SharedAlert);
  app.component('SharedAvatar', SharedAvatar);
  app.component('SharedBack', SharedBack);
  app.component('SharedBtn', SharedBtn);
  app.component('SharedComboboxInput', SharedComboBox);
  app.component('SharedLabeledText', SharedLabeledText);
  app.component('SharedLazyIterator', SharedLazyIterator);
  app.component('SharedLoading', SharedLoading);
  app.component('SharedModal', SharedModal);
  app.component('SharedNotFound', SharedNotFound);
  app.component('SharedOverlay', SharedOverlay);
  app.component('SharedProgressCircular', SharedProgressCircular);
  app.component('SharedRefresh', SharedRefresh);
  app.component('SharedScrollList', SharedScrollList);
  app.component('SharedTeleport', SharedTeleport);
  app.component('SharedTextInput', SharedTextInput);
  app.component('SharedTooltip', SharedTooltip);
  app.component('SharedTextTruncated', SharedTextTruncated);
  app.component('BaseDialog', Dialog);
}

function loadPlugins() {
  app.use(piniaPlugin);
  app.use(ctxPlugin);
  app.use(envPlugin);
  app.use(routerPlugin);
  app.use(vuetify);
  app.use(PortalVue);
  app.use(
    // gmap richiede vue 3.5.35 mentre noi abbiamo la 3.5.38
    // @ts-expect-error Argument of type 'FunctionPlugin' is not assignable to parameter of type 'Plugin<any[], any[]>'.
    createGmapVuePlugin({
      load: {
        key: env.VITE_GOOGLE_MAPS_APIKEY,
        libraries: 'places,drawing',
      },
    }),
  );
}

function loadLayouts() {
  app.component('PrivateLayout', PrivateLayout);
  app.component('PublicLayout', PublicLayout);
  app.component('ErrorLayout', ErrorLayout);
}

function loadDirectives() {
  app.directive('m_intersect', intersect);
  app.directive('click-outside', clickOutside);
  app.directive('ripple', ripple);
  app.directive('scrollbar-active-class', scrollbarActiveClass);
  app.directive('on-scroll-end', onScrollEnd);
}

function mountApp() {
  app.mount('#app');
}

/**
 * The public page of a building is {{clientBaseUrl}}/buildings.html?id={{id}}
 * QR codes redirect to {{clientBaseUrl}}/buildings/{{id}}. This page belongs to the
 * index page of Client and thus requires authentication. Users not authenticated will
 * be redirected to Go.
 */
const _url = new URL(window.location.href);
const _to = _url.searchParams.get('to');
const getPathSegments = (url: string) => new URL(url).pathname.substring(1).split('/');

if (_to && getPathSegments(_to)[0] === 'buildings') {
  const id = getPathSegments(_to)[1];
  window.open(`${env.VUE_APP_AMMINISTROIO_URL}/buildings.html?id=${id}`, '_self');
}
else {
  pipe(config, loadPlugins, loadDirectives, loadSharedComponents, loadLayouts, mountApp)();
}

versionListener.init(() => {
  dialogService
    .showDialog({
      title: 'Attenzione',
      description: `È disponibile una nuova versione di ${env.VUE_APP_PRODUCT_DISPLAY_NAME}. Per continuare, ricarica la pagina.`,
      successButtonText: 'Ricarica',
      showErrorButton: false,
    })
    .then(() => location.reload());
});
